/**
 * Geometry the instanced overview silhouette shares with the full equipment kit.
 *
 * Both detail levels have to agree on where a tool's box is and where its docks
 * sit, or a tool visibly shifts as it crosses the LOD threshold. Everything here
 * therefore resolves through the same helpers `ReferenceProcessMachine` uses
 * rather than re-deriving the layout with its own constants.
 */
import {
  clampProcessLoadPortCount,
  getProcessEquipmentDeck,
  getProcessEquipmentPortPoses,
} from './amhs/Equipment/equipmentPortTransfer';

export type EquipmentRenderDescriptor = {
  id: string;
  originX: number;
  originY: number;
  /** 2D footprint width. */
  width: number;
  /** 2D footprint depth (world Z). */
  height: number;
  /**
   * Rendered vertical extent, i.e. what the full kit actually builds to.
   * Producers must pass `resolveEquipmentRenderHeight(...)` rather than the raw
   * extrusion depth, or the silhouette ends up shorter than the kit.
   */
  depth: number;
  rotationDeg: number;
  kind: 'equipment' | 'stocker';
  /** Port-facing rotation the kit applies inside the footprint. */
  facingRotationY?: number;
  /** Whether that facing swaps the kit's width/depth, as the kit adapter does. */
  swapsFootprintAxes?: boolean;
  /**
   * Declared dock count, from the same port list the kit and 2D use. Omitting
   * it falls back to the kit's own two-dock default rather than drawing none.
   */
  loadPortCount?: number;
  /** Along-face dock fractions, so a seat sits where the kit puts it. */
  portFractions?: readonly number[];
  /** Stocker inventory rendered in the shared full-model rack. */
  storedFoupsCount?: number;
};

/** Kit-local dimensions, mirroring `AmhsEquipmentFromLayout3D`. */
export type KitBox = { w: number; h: number; d: number; rotationY: number };

export function resolveKitBox(descriptor: EquipmentRenderDescriptor): KitBox {
  // The stocker adapter places its rack on the raw footprint with no facing
  // rotation, so only process tools follow the port-facing swap.
  const swaps = descriptor.kind === 'equipment' && (descriptor.swapsFootprintAxes ?? false);
  return {
    w: Math.max(swaps ? descriptor.height : descriptor.width, 1),
    h: Math.max(descriptor.depth, 1),
    d: Math.max(swaps ? descriptor.width : descriptor.height, 1),
    rotationY: descriptor.kind === 'equipment' ? (descriptor.facingRotationY ?? 0) : 0,
  };
}

export type SilhouettePortLayout = {
  /** Kit-local seat X per dock, matching `getProcessEquipmentPortPoses`. */
  seats: number[];
  deckY: number;
  nestWidth: number;
  trayDepth: number;
  trayCenterZ: number;
  doorW: number;
  doorH: number;
  doorD: number;
  doorZ: number;
  dockY: number;
};

const layoutCache = new WeakMap<EquipmentRenderDescriptor, SilhouettePortLayout | null>();

/**
 * Dock layout for one tool, or null when it has no process dock bank.
 *
 * The pose helpers are not cheap and several instanced batches ask for the same
 * answer, so results are cached against the descriptor object. Producers rebuild
 * descriptors whenever the layout changes, which retires the entry.
 */
export function resolveSilhouettePortLayout(
  descriptor: EquipmentRenderDescriptor,
  box: KitBox
): SilhouettePortLayout | null {
  const cached = layoutCache.get(descriptor);
  if (cached !== undefined) return cached;

  let layout: SilhouettePortLayout | null = null;
  if (descriptor.kind === 'equipment') {
    const ports = clampProcessLoadPortCount(descriptor.loadPortCount ?? 2);
    const deck = getProcessEquipmentDeck(box.w, box.h, box.d, ports, descriptor.portFractions);
    const poses = getProcessEquipmentPortPoses(
      box.w,
      box.h,
      box.d,
      ports,
      descriptor.portFractions
    );
    const first = poses[0]!;
    layout = {
      seats: poses.map((pose) => pose.bay[0]),
      deckY: deck.deckY,
      nestWidth: deck.nestWidth,
      trayDepth: deck.trayDepth,
      trayCenterZ: deck.trayCenterZ,
      // The kit hangs one bank-wide door over the mouth rather than one per
      // dock, so the silhouette draws the same single panel.
      doorW: box.w * 0.92 * 0.96,
      doorH: Math.max(first.shutterSize[1], box.h * 0.28),
      doorD: Math.max(first.shutterSize[2], box.d * 0.03),
      doorZ: first.shutter[2],
      dockY: box.h * 0.2,
    };
  }
  layoutCache.set(descriptor, layout);
  return layout;
}
